/**
 * NoteEyebrow — replaces the tracked all-caps eyebrow pattern used by
 * every section on the landing page with a hand-written margin
 * annotation in Caveat.
 *
 * Drop-in for anywhere you'd previously have written:
 *   <span className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink">
 *     Ways to give
 *   </span>
 *
 * Replace with:
 *   <NoteEyebrow>ways to give</NoteEyebrow>
 *
 * The whole eyebrow gets rotated a couple of degrees off-axis so it
 * reads like a real ink annotation in the margin of a magazine, and
 * leads with a small tangerine arrow that points at the label.
 * Lowercase copy is intentional — handwriting sits more naturally on
 * the baseline in lowercase.
 *
 * `arrow` accepts a boolean or a custom glyph (default: "→"). Set to
 * `false` to omit for eyebrows that don't want the annotation mark.
 *
 * `accent` (e.g. "bg-cobalt") swaps the arrow for a single palette dash:
 * the section's one quiet color note, matched to its EmSwash underline.
 */
export const NoteEyebrow = ({
  children,
  arrow = true,
  accent,
  className = "",
  ...rest
}) => (
  <div
    className={`inline-flex origin-left -rotate-2 items-center ${accent ? "gap-3" : "gap-1.5"} ${className}`}
    data-testid="note-eyebrow"
    {...rest}
  >
    {accent ? (
      <span aria-hidden="true" className={`h-2 w-7 shrink-0 rounded-full ${accent}`} data-testid="note-eyebrow-dash" />
    ) : arrow ? (
      <span
        aria-hidden="true"
        className="-mt-0.5 font-caveat text-3xl leading-none text-tangerine"
      >
        {typeof arrow === "string" ? arrow : "→"}
      </span>
    ) : null}
    <span className="font-caveat text-3xl font-medium leading-none text-olive">
      {children}
    </span>
  </div>
);
